import { Link } from 'react-router-dom';
import { money, todayIso } from '../api';
import { useApi } from '../useApi';
import { ErrorBox, Loading, Status, Tile } from '../ui';

type Dash = {
  date: string;
  orders: Array<{ status: string; count: number; totalKop: string }>;
  routes: Array<{ id: string; driver: { name: string } | null; status: string; stops: number; done: number; loadedFull: number }>;
  money: { chargedKop: string; paidKop: string };
  debt: { clients: number; totalKop: string };
  bottlesAtClients: number;
  newClients: number;
  unreadConversations: number;
};

export function Dashboard() {
  const { data, error, loading } = useApi<Dash>(`/reports/dashboard?date=${todayIso()}`);

  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  if (!data) return null;

  const total = data.orders.reduce((s, o) => s + o.count, 0);
  const delivered = data.orders.find((o) => o.status === 'DELIVERED')?.count ?? 0;

  return (
    <>
      <h1>Сводка на сегодня</h1>

      <div className="grid c4" style={{ marginBottom: 16 }}>
        <Tile label="Заказов на день" value={total} sub={`доставлено ${delivered}`} />
        <Tile label="Начислено" value={money(data.money.chargedKop)} sub="по проводкам за день" />
        <Tile label="Получено" value={money(data.money.paidKop)} tone="prepaid" />
        <Tile
          label="Долг клиентов"
          value={money(data.debt.totalKop)}
          sub={`${data.debt.clients} клиентов`}
          tone="debt"
        />
      </div>

      <div className="grid c4" style={{ marginBottom: 16 }}>
        <Tile label="Тары у клиентов" value={`${data.bottlesAtClients} шт.`} sub="оборотная тара в поле" />
        <Tile label="Новых клиентов" value={data.newClients} sub="за сегодня" />
        <Tile
          label="Непрочитанных"
          value={data.unreadConversations}
          sub={<Link to="/inbox">перейти в сообщения</Link>}
        />
        <Tile label="Маршрутов" value={data.routes.length} sub={<Link to="/routes">открыть</Link>} />
      </div>

      <div className="row">
        <div className="card" style={{ flex: '1 1 420px' }}>
          <h2>Заказы дня по состояниям</h2>
          {data.orders.length === 0 ? (
            <div className="muted">На сегодня заказов нет</div>
          ) : (
            <table>
              <tbody>
                {data.orders.map((o) => (
                  <tr key={o.status}>
                    <td>
                      <Status value={o.status} />
                    </td>
                    <td className="num">{o.count}</td>
                    <td className="num">{money(o.totalKop)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card" style={{ flex: '1 1 420px' }}>
          <h2>Маршруты</h2>
          {data.routes.length === 0 ? (
            <div className="muted">Маршруты на сегодня ещё не собраны</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Водитель</th>
                  <th>Состояние</th>
                  <th className="num">Точки</th>
                  <th className="num">Загрузка</th>
                </tr>
              </thead>
              <tbody>
                {data.routes.map((r) => (
                  <tr key={r.id}>
                    <td>{r.driver?.name ?? '— не назначен'}</td>
                    <td>
                      <Status value={r.status} />
                    </td>
                    <td className="num">
                      {r.done} / {r.stops}
                    </td>
                    <td className="num">{r.loadedFull} бут.</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
